'use client'

import * as React from 'react'
import { Building2, ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { PALETTES } from './helpers'

// Solid warm gradient placeholder standing in for a listing photo — ui.jsx:31-42.
export interface PhotoBlockProps extends React.HTMLAttributes<HTMLDivElement> {
  palette?: number
  seed?: number
}

export function PhotoBlock({ palette = 0, seed = 0, className, style, children, ...props }: PhotoBlockProps) {
  const p = PALETTES[(palette + seed) % PALETTES.length]
  const angle = 115 + ((palette * 31 + seed * 47) % 70)
  return (
    <div
      className={cn('relative overflow-hidden', className)}
      style={{ background: `linear-gradient(${angle}deg, ${p[0]}, ${p[1]})`, ...style }}
      {...props}
    >
      <div className="absolute inset-0 flex items-center justify-center">
        <Building2 size={44} color="rgba(60,40,25,0.14)" />
      </div>
      {children}
    </div>
  )
}

// `.photo-arrow` + `.hover-reveal` from theme.css: 32px white/92 circle that
// fades in on frame hover and springs to 1.1 scale on its own hover.
const arrowClsBase =
  'absolute top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full ' +
  'bg-white/[0.92] text-[#1C1917] shadow-e1 ' +
  '[transition:transform_0.15s_cubic-bezier(0.3,1.3,0.4,1),opacity_0.18s_cubic-bezier(0.2,0,0,1),background_0.18s] ' +
  'hover:scale-110 hover:bg-white'
// Hover-revealed by default (desktop grid cards, where the arrows would be
// visual noise across 20 tiles). Surfaces that are small, transient or
// pointer-fiddly pass `arrowsAlwaysVisible` — on those, hidden arrows read as
// "you cannot page these photos" (user 2026-08-16, map mini-card).
const arrowCls = arrowClsBase + ' opacity-0 group-hover/photos:opacity-100'
const arrowClsAlways = arrowClsBase + ' opacity-100 shadow-e2'

interface PhotoFrameProps {
  n: number
  idx: number
  go: (d: number) => (e: React.MouseEvent<HTMLButtonElement>) => void
  /** Advance without an event — the touch-swipe path. */
  onSwipe?: (d: number) => void
  aspect?: string
  radius?: number
  fillHeight?: boolean
  overlay?: React.ReactNode
  /** Show the pager arrows without needing a hover. */
  arrowsAlwaysVisible?: boolean
  className?: string
  children: React.ReactNode
}

// Shared photo chrome (arrows, counter pill, dot strip, overlay) — ui.jsx:57-79.
// The arrows are hover-revealed (desktop); on touch, a horizontal swipe pages
// the carousel — without it phones had NO way to browse photos in place (the
// dot strip is decorative and tap opens the gallery).
function PhotoFrame({
  n,
  idx,
  go,
  onSwipe,
  aspect = '4 / 3',
  radius = 0,
  fillHeight = false,
  overlay,
  arrowsAlwaysVisible = false,
  className,
  children,
}: PhotoFrameProps) {
  const arrow = arrowsAlwaysVisible ? arrowClsAlways : arrowCls
  const touchRef = React.useRef<{ x: number; y: number } | null>(null)
  const handleTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0]
    if (t) touchRef.current = { x: t.clientX, y: t.clientY }
  }
  const handleTouchEnd = (e: React.TouchEvent) => {
    const start = touchRef.current
    touchRef.current = null
    if (!start || n <= 1 || !onSwipe) return
    const t = e.changedTouches[0]
    if (!t) return
    const dx = t.clientX - start.x
    const dy = t.clientY - start.y
    // Horizontal-intent gate: >40px travel and clearly more X than Y (vertical
    // page scrolling never triggers paging — touchmove is left native).
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      // Suppress the synthetic click so a swipe doesn't ALSO open the
      // gallery / navigate (the card's click handlers live on ancestors).
      e.preventDefault()
      onSwipe(dx < 0 ? 1 : -1)
    }
  }
  return (
    <div
      // bg-surface-container behind the (lazy) photo: the frame is transparent,
      // so until an image decoded the card showed a ~257px blank white block
      // while scrolling a phone. A neutral fill reads as "loading", not "empty".
      className={cn('group/photos relative overflow-hidden bg-surface-container', className)}
      style={{
        aspectRatio: fillHeight ? undefined : aspect,
        height: fillHeight ? '100%' : undefined,
        borderRadius: radius,
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {children}
      {n > 1 && (
        <>
          <button type="button" className={cn(arrow, 'left-2')} onClick={go(-1)} title="Previous photo" aria-label="Previous photo">
            <ChevronLeft size={20} />
          </button>
          <button type="button" className={cn(arrow, 'right-2')} onClick={go(1)} title="Next photo" aria-label="Next photo">
            <ChevronRight size={20} />
          </button>
          <div className="absolute bottom-[10px] right-3 whitespace-nowrap rounded-full bg-[rgba(26,23,23,0.7)] px-[9px] py-[3px] font-mono text-[11.5px] font-bold text-white">
            {idx + 1} / {n}
          </div>
          <div className="absolute bottom-[13px] left-3 flex gap-1">
            {Array.from({ length: Math.min(n, 6) }).map((_, i) => (
              <span
                key={i}
                className={cn(
                  'h-[5px] rounded-[3px] transition-[width] duration-[250ms] ease-spatial',
                  i === idx % 6 ? 'w-4 bg-white' : 'w-[5px] bg-white/55'
                )}
              />
            ))}
          </div>
        </>
      )}
      {overlay}
    </div>
  )
}

// Photo region with hover arrows, counter and dots (mobile carousel anatomy,
// web hover affordances) — ui.jsx:46-81. `overlay` renders on top.
export interface PhotosProps {
  count?: number
  palette?: number
  aspect?: string
  radius?: number
  fillHeight?: boolean
  overlay?: React.ReactNode
  onIndexChange?: (index: number) => void
  className?: string
}

export function Photos({ count = 1, palette = 0, aspect, radius, fillHeight, overlay, onIndexChange, className }: PhotosProps) {
  const [idx, setIdx] = React.useState(0)
  const n = Math.max(1, count)
  const advance = (d: number) => {
    const next = (idx + d + n) % n
    setIdx(next)
    onIndexChange?.(next)
  }
  const go = (d: number) => (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    advance(d)
  }
  return (
    <PhotoFrame n={n} idx={idx} go={go} onSwipe={advance} aspect={aspect} radius={radius} fillHeight={fillHeight} overlay={overlay} className={className}>
      <PhotoBlock palette={palette} seed={idx} className="absolute inset-0" />
    </PhotoFrame>
  )
}

// Same chrome over real listing images. Lazy-loads and falls back to the
// gradient PhotoBlock when an image fails (or a URL is missing).
export interface RealPhotosProps {
  images: string[]
  alt?: string
  /** Palette for the error-fallback gradient. */
  palette?: number
  aspect?: string
  radius?: number
  fillHeight?: boolean
  overlay?: React.ReactNode
  /**
   * Controlled index — pass with `onIndexChange` when the parent owns
   * navigation (e.g. keyboard shortcuts). Omit for uncontrolled (default).
   */
  index?: number
  onIndexChange?: (index: number) => void
  /** Show pager arrows without a hover (small/transient surfaces). */
  arrowsAlwaysVisible?: boolean
  className?: string
}

export function RealPhotos({
  images,
  alt = '',
  palette = 0,
  aspect,
  radius,
  fillHeight,
  overlay,
  index,
  onIndexChange,
  arrowsAlwaysVisible,
  className,
}: RealPhotosProps) {
  const [internalIdx, setInternalIdx] = React.useState(0)
  const [failed, setFailed] = React.useState<Record<number, boolean>>({})
  const n = Math.max(1, images.length)
  const idx = index ?? internalIdx
  const advance = (d: number) => {
    const next = (idx + d + n) % n
    if (index == null) setInternalIdx(next)
    onIndexChange?.(next)
  }
  const go = (d: number) => (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    advance(d)
  }
  const src = images[idx]
  const broken = !src || failed[idx]
  return (
    <PhotoFrame n={n} idx={idx} go={go} onSwipe={advance} aspect={aspect} radius={radius} fillHeight={fillHeight} overlay={overlay} arrowsAlwaysVisible={arrowsAlwaysVisible} className={className}>
      {broken ? (
        <PhotoBlock palette={palette} seed={idx} className="absolute inset-0" />
      ) : (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
          onError={() => setFailed((f) => ({ ...f, [idx]: true }))}
        />
      )}
    </PhotoFrame>
  )
}
